import {
  getMenulyticsReport,
  getRefreshToken,
  MenulyticsReport,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { isPending } from '../menuHealthFormat';

const SOCKET_URL = process.env.NX_API_URL;

/** How often running reports are checked on; nothing is read while all is well. */
const TICK_MS = 10_000;

/**
 * A running report that has not moved for this long is read once by hand,
 * even with the socket up. Menulytics moves every few seconds, so a silent
 * row means an event went missing (seen on dev, 2026-10-12: stuck at 68%
 * while the backend already had it completed).
 */
const STALE_MS = 30_000;

type Options = {
  locationId: string;
  enabled: boolean;
  reports: MenulyticsReport[];
  /** Replaces a row by id. */
  patch: (report: MenulyticsReport) => void;
  /** Reloads the list after a reconnect, when events may have been missed. */
  refetch: () => void;
};

/**
 * Live progress of the reports this admin started. The backend pushes
 * `menulytics_report_updated` on the menu socket to the admin who generated
 * the report, with no subscribe step.
 *
 * The event carries no location id, so a report is only patched when its id
 * is already in this location's list; a report from another tab's location is
 * left alone. While the socket is down the running reports are read one by
 * one instead, which the backend allows as a fallback only - and so is a
 * running report the socket has gone quiet about.
 */
export const useMenulyticsReportSocket = ({
  locationId,
  enabled,
  reports,
  patch,
  refetch,
}: Options) => {
  const [connected, setConnected] = useState(false);

  // The handlers live for the socket's lifetime; reading the latest list and
  // callbacks through a ref keeps them from reconnecting on every row change.
  const latest = useRef({ reports, patch, refetch });
  latest.current = { reports, patch, refetch };

  useEffect(() => {
    const refreshToken = getRefreshToken();
    if (!enabled || !refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/menu',
      transports: ['websocket'],
      auth: { authorization: refreshToken },
    });

    let connectedBefore = false;
    socket.on('connect', () => {
      setConnected(true);
      if (connectedBefore) latest.current.refetch();
      connectedBefore = true;
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('menulytics_report_updated', (report: MenulyticsReport) => {
      if (latest.current.reports.some((row) => row.id === report.id)) {
        latest.current.patch(report);
      }
    });

    return () => {
      socket.disconnect();
      setConnected(false);
    };
  }, [enabled]);

  // When each running report last changed, by id and by what it showed.
  const seen = useRef(new Map<number, { key: string; at: number }>());
  reports.filter(isPending).forEach((report) => {
    const key = `${report.status}:${report.progressPercent}`;
    if (seen.current.get(report.id)?.key !== key) {
      seen.current.set(report.id, { key, at: Date.now() });
    }
  });

  const pendingIds = reports
    .filter(isPending)
    .map((report) => report.id)
    .join(',');

  useEffect(() => {
    if (!enabled || !pendingIds) return;

    const timer = window.setInterval(() => {
      pendingIds.split(',').forEach(async (value) => {
        const id = Number(value);
        const last = seen.current.get(id);
        const stale = !last || Date.now() - last.at >= STALE_MS;
        if (connected && !stale) return;
        // Counts as a change, so a quiet report is read every STALE_MS, not
        // on every tick.
        seen.current.set(id, { key: last?.key ?? '', at: Date.now() });
        try {
          const { data } = await getMenulyticsReport(locationId, id);
          latest.current.patch(data.data);
        } catch {
          // The next round tries again; a dead fallback is not worth a toast.
        }
      });
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [enabled, connected, pendingIds, locationId]);
};
